// SAFE-DRAFT: bringing the user's unsaved changes onto a newer version of the draft (another user
// saved meanwhile, or the changes were kept across a sign-in), without silently dropping either
// side. A three-way merge: what the user changed since the version they started from is applied to
// the newer version; where the other side changed the same thing differently, the user chooses
// (their own change by default). Also: giving new findings and meter readings the ids the server
// assigned, so the next automatic save updates them instead of adding them again.
import type { HandoverDocument } from '@sivan/shared';
import { type Draft, type DraftFinding, type DraftMeter, draftFrom } from './handover-draft';

export interface MergeConflict {
  readonly id: string;
  /** What both changed, in Hebrew (for example "תוצאת הבדיקה: חשמל · שקעים"). */
  readonly label: string;
  readonly mine: string;
  readonly theirs: string;
  /** Applies the user's own change on top of the merged draft. */
  readonly applyMine: (draft: Draft) => Draft;
}

export interface MergeResult {
  /** The newer version with every non-conflicting change of the user (conflicts: the other side). */
  readonly draft: Draft;
  readonly conflicts: readonly MergeConflict[];
  /** Number of the user's changes carried over. */
  readonly applied: number;
}

const STATUS: Readonly<Record<string, string>> = {
  unchecked: 'טרם נבדק',
  ok: 'תקין',
  defect: 'לא תקין',
  not_relevant: 'לא רלוונטי',
  pending: 'טרם נבדק מחדש',
  repaired: 'תוקן',
  still_defective: 'עדיין לא תקין',
  open: 'פתוח',
  resolved: 'תוקן',
  withdrawn: 'נרשם בטעות',
};
const said = (status: string | undefined) =>
  status === undefined ? 'הוסר' : (STATUS[status] ?? status);

/** The fields of a finding that a save sends (keys and texts recorded on it aside). */
const findingValue = (f: DraftFinding) =>
  JSON.stringify({
    areaId: f.areaId,
    componentId: f.componentId,
    contractorId: f.contractorId,
    workCategoryId: f.workCategoryId,
    description: f.description.trim(),
    inspectedOn: f.inspectedOn,
    photoIds: f.photos.map((p) => p.id),
    status: f.status,
    resolutionNote: f.status === 'open' ? '' : f.resolutionNote.trim(),
  });
const meterValue = (m: DraftMeter) =>
  JSON.stringify([m.meterType.trim(), m.meterNumber.trim(), m.reading.trim(), m.note.trim()]);
const json = (value: unknown) => JSON.stringify(value);

/** Applies `mine` (changed since `base`) onto `theirs`: one record of a keyed collection. */
function mergeRecord<T>(
  base: Readonly<Record<string, T>>,
  mine: Readonly<Record<string, T>>,
  theirs: Readonly<Record<string, T>>,
  label: (id: string) => string,
  show: (value: T | undefined) => string,
  set: (draft: Draft, id: string, value: T) => Draft,
  out: { draft: Draft; conflicts: MergeConflict[]; applied: number },
  prefix: string,
) {
  for (const [id, value] of Object.entries(mine)) {
    if (!(id in theirs)) continue;
    const before = base[id];
    if (json(value) === json(before)) continue;
    const other = theirs[id];
    if (json(other) === json(before) || json(other) === json(value)) {
      out.draft = set(out.draft, id, value);
      out.applied += 1;
    } else
      out.conflicts.push({
        id: `${prefix}:${id}`,
        label: label(id),
        mine: show(value),
        theirs: show(other),
        applyMine: (d) => set(d, id, value),
      });
  }
}

export function mergeDrafts(
  baseDoc: HandoverDocument,
  mine: Draft,
  theirsDoc: HandoverDocument,
): MergeResult {
  const base = draftFrom(baseDoc);
  const theirs = draftFrom(theirsDoc);
  const out = { draft: theirs, conflicts: [] as MergeConflict[], applied: 0 };

  // Handover date.
  if (mine.handoverDate !== base.handoverDate) {
    if (theirs.handoverDate === base.handoverDate || theirs.handoverDate === mine.handoverDate) {
      out.draft = { ...out.draft, handoverDate: mine.handoverDate };
      out.applied += 1;
    } else
      out.conflicts.push({
        id: 'date',
        label: 'תאריך המסירה',
        mine: mine.handoverDate || 'ללא',
        theirs: theirs.handoverDate || 'ללא',
        applyMine: (d) => ({ ...d, handoverDate: mine.handoverDate }),
      });
  }

  const itemName = (id: string) => {
    const item = theirsDoc.inspection.items.find((i) => i.id === id);
    return item === undefined ? 'פריט' : `${item.area} · ${item.workCategory} · ${item.name}`;
  };
  mergeRecord(
    base.results,
    mine.results,
    theirs.results,
    (id) => `תוצאת בדיקה: ${itemName(id)}`,
    (v) => said(v),
    (d, id, v) => ({ ...d, results: { ...d.results, [id]: v } }),
    out,
    'result',
  );
  const checkName = (id: string) =>
    theirsDoc.generalChecks.items.find((c) => c.id === id)?.name ?? 'בדיקה כללית';
  mergeRecord(
    base.checks,
    mine.checks,
    theirs.checks,
    (id) => `בדיקה כללית: ${checkName(id)}`,
    (v) =>
      v === undefined
        ? 'הוסר'
        : [said(v.status), v.quantity === '' ? '' : `כמות ${v.quantity}`, v.note.trim()]
            .filter((t) => t !== '')
            .join(' · '),
    (d, id, v) => ({ ...d, checks: { ...d.checks, [id]: v } }),
    out,
    'check',
  );
  const reinspected = (id: string) => {
    const r = theirsDoc.reinspection?.items.find((i) => i.finding.id === id);
    return r === undefined ? 'ליקוי' : `${r.finding.area} · ${r.finding.component}`;
  };
  // Reinspections: compare what is saved (result, date, note, photo ids).
  const reinspectionValues = (d: Draft) =>
    Object.fromEntries(
      Object.entries(d.reinspections).map(([id, r]) => [
        id,
        { ...r, note: r.note.trim(), photos: r.photos.map((p) => p.id) },
      ]),
    );
  const mineReinspections = reinspectionValues(mine);
  mergeRecord(
    reinspectionValues(base),
    mineReinspections,
    reinspectionValues(theirs),
    (id) => `בדיקה חוזרת: ${reinspected(id)}`,
    (v) =>
      v === undefined ? 'הוסר' : [said(v.result), v.note].filter((t) => t !== '').join(' · '),
    (d, id) => {
      const value = mine.reinspections[id];
      return value === undefined ? d : { ...d, reinspections: { ...d.reinspections, [id]: value } };
    },
    out,
    'reinspection',
  );

  // Findings and meter readings: added ones are kept; changed and removed ones apply when the other
  // side left them as they were.
  const list = <T extends { key: string; id: string | null }>(
    field: 'findings' | 'meters',
    get: (d: Draft) => readonly T[],
    put: (d: Draft, items: readonly T[]) => Draft,
    baseList: readonly T[],
    mineList: readonly T[],
    theirsList: readonly T[],
    value: (item: T) => string,
    name: (item: T) => string,
  ) => {
    const baseById = new Map(baseList.map((i) => [i.id, i]));
    const theirsById = new Map(theirsList.map((i) => [i.id, i]));
    const replace = (d: Draft, item: T): Draft =>
      put(
        d,
        get(d).map((i) => (i.id === item.id ? item : i)),
      );
    const remove = (d: Draft, id: string): Draft =>
      put(
        d,
        get(d).filter((i) => i.id !== id),
      );
    const append = (d: Draft, item: T): Draft => put(d, [...get(d), item]);
    for (const item of mineList) {
      if (item.id === null) {
        out.draft = append(out.draft, item);
        out.applied += 1;
        continue;
      }
      const before = baseById.get(item.id);
      if (before === undefined || value(item) === value(before)) continue;
      const other = theirsById.get(item.id);
      if (other === undefined) {
        // Removed by the other side: the user may keep theirs as a new entry (without photos,
        // which were removed with it).
        const copy = { ...item, key: `restored-${item.key}`, id: null, photos: [] };
        out.conflicts.push({
          id: `${field}:${item.id}`,
          label: `${field === 'findings' ? 'ליקוי' : 'מונה'} שהוסר בידי משתמש אחר: ${name(item)}`,
          mine: 'לשמור את הגרסה שלי',
          theirs: 'הוסר',
          applyMine: (d) => append(d, copy),
        });
      } else if (value(other) === value(before) || value(other) === value(item)) {
        out.draft = replace(out.draft, item);
        out.applied += 1;
      } else
        out.conflicts.push({
          id: `${field}:${item.id}`,
          label: `${field === 'findings' ? 'ליקוי' : 'מונה'}: ${name(item)}`,
          mine: name(item),
          theirs: name(other),
          applyMine: (d) => replace(d, item),
        });
    }
    const kept = new Set(mineList.map((i) => i.id));
    for (const before of baseList) {
      if (before.id === null || kept.has(before.id)) continue;
      const other = theirsById.get(before.id);
      if (other === undefined) continue;
      const id = before.id;
      if (value(other) === value(before)) {
        out.draft = remove(out.draft, id);
        out.applied += 1;
      } else
        out.conflicts.push({
          id: `${field}:${id}`,
          label: `${field === 'findings' ? 'ליקוי' : 'מונה'} שהסרת ומשתמש אחר שינה: ${name(other)}`,
          mine: 'הסרה',
          theirs: 'להשאיר עם השינוי שלו',
          applyMine: (d) => remove(d, id),
        });
    }
  };
  list<DraftFinding>(
    'findings',
    (d) => d.findings,
    (d, findings) => ({ ...d, findings }),
    base.findings,
    mine.findings,
    theirs.findings,
    findingValue,
    (f) => `${f.area} · ${f.component}: ${f.description.trim()}`,
  );
  list<DraftMeter>(
    'meters',
    (d) => d.meters,
    (d, meters) => ({ ...d, meters }),
    base.meters,
    mine.meters,
    theirs.meters,
    meterValue,
    (m) => `${m.meterType} ${m.reading}`.trim(),
  );
  return out;
}

/** The final draft: the merge, with the user's own change wherever `keepMine` says so. */
export const resolveConflicts = (result: MergeResult, keepMine: ReadonlySet<string>): Draft =>
  result.conflicts.filter((c) => keepMine.has(c.id)).reduce((d, c) => c.applyMine(d), result.draft);

/**
 * After a save of `sent`, gives the findings and meter readings that were new in it the ids the
 * server assigned (the saved document lists them in the order sent), keeping any change the user
 * made while the save was in flight.
 */
export function adoptSavedIds(sent: Draft, current: Draft, saved: HandoverDocument): Draft {
  const ids = (
    sentList: readonly { key: string; id: string | null }[],
    savedIds: readonly string[],
  ) => {
    const map = new Map<string, string>();
    sentList.forEach((item, index) => {
      const id = savedIds[index];
      if (item.id === null && id !== undefined) map.set(item.key, id);
    });
    return map;
  };
  const findingIds = ids(
    sent.findings,
    saved.findings.map((f) => f.id),
  );
  const meterIds = ids(
    sent.meters,
    saved.meters.map((m) => m.id),
  );
  return {
    ...current,
    findings: current.findings.map((f) => {
      const id = findingIds.get(f.key);
      return id === undefined ? f : { ...f, id };
    }),
    meters: current.meters.map((m) => {
      const id = meterIds.get(m.key);
      return id === undefined ? m : { ...m, id };
    }),
  };
}
